import { type ReactNode, useState } from 'react';
import { Plus } from 'lucide-react';
import { EmbedChip, detectProvider } from './embed-chip';

type Props = {
  urls: string[];
  onChange: (urls: string[]) => void;
};

const INPUT =
  'flex-1 rounded-md border border-white/[0.06] bg-surface px-sm py-1 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-red/40 focus:outline-none';
const ADD_BTN =
  'inline-flex items-center gap-xs rounded-md border border-white/[0.06] px-sm py-1 text-[12px] text-text-secondary hover:border-brand-red/40 hover:text-brand-red';

function useAddEmbed(urls: string[], onChange: (urls: string[]) => void) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const submit = (): void => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (!detectProvider(trimmed)) return setError('Solo YouTube y Loom');
    if (urls.includes(trimmed)) return setError('Ya añadido');
    onChange([...urls, trimmed]);
    setDraft('');
    setError('');
  };
  return { draft, setDraft, error, submit };
}

function AddRow(p: {
  draft: string;
  setDraft: (v: string) => void;
  submit: () => void;
}): ReactNode {
  return (
    <div className="flex gap-sm">
      <input
        type="url"
        placeholder="https://youtube.com/watch?v=…"
        value={p.draft}
        onChange={(e) => p.setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          p.submit();
        }}
        className={INPUT}
      />
      <button type="button" onClick={p.submit} className={ADD_BTN}>
        <Plus size={12} /> Añadir
      </button>
    </div>
  );
}

export function ComposerEmbedAdder({ urls, onChange }: Props): ReactNode {
  const { draft, setDraft, error, submit } = useAddEmbed(urls, onChange);
  return (
    <div className="space-y-sm">
      <label className="text-[12px] uppercase tracking-wide text-text-tertiary">
        Embeds (YouTube / Loom)
      </label>
      <AddRow draft={draft} setDraft={setDraft} submit={submit} />
      {error && <p className="text-[11px] text-error">{error}</p>}
      {urls.length > 0 && (
        <div className="flex flex-wrap gap-xs">
          {urls.map((u, i) => (
            <EmbedChip
              key={`${u}-${i}`}
              url={u}
              onRemove={() => onChange(urls.filter((x) => x !== u))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
